import { useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Helpers } from '../core/src/helpers';
import useProductPrice from './useProductPrice';
import {
    addToCart,
    addMultipleToCart,
    launchCartFlyout,
    addCTOToCart,
    updateCart,
    updateCartBySKU,
} from '../utility/utility-actions';
import { confirmAddToCart } from '../utility/components/add-to-cart-confirm/add-to-cart-confirm-actions';
import { fetchXSellProducts } from '../xsell/xsell-actions';
import { fetchPrices } from '../product/product-actions';
import { getAddon } from '../metrics/metrics-helpers';

import { setAddOns, setSoftBundle } from '../product/components/product-configurations/addon-actions';
import { setCTOConfiguration } from '../product/components/product-configurations/cto-actions';
import { getCTOLink } from '../core/src/etr-api-client/cto';
import useCartState from './useCartState';
import { getSerialNumber } from '../pdp/components-v2/pdp-details/cta';
import { getDefaultState } from '../product/components/product-configurations';

const EMPTY_OBJECT = {};
const suppliesSearchUrl = `${process.env.BASENAME}/cv/supplies-search`;

/**
 * Hook that encapsulates all add-to-cart and purchase logic for the PDP.
 * Uses Redux hooks for state/dispatch, and useMemo/useCallback for performance optimization.
 *
 * @param {object} params - Hook configuration
 * @param {string} params.baseProductSku - SKU for tracking the base product in metrics
 * @param {string} params.xsellType - Type of cross-sell being used
 * @param {Function} params.onClick - Custom onClick callback to wrap around purchase handlers
 * @param {string} params.jumpid - Jump ID for URL navigation
 * @param {object} [params.additionalParams={}] - Additional URL parameters
 * @param {boolean} [params.isMultiple=false] - Whether multiple quantities can be selected
 * @param {number} [params.qty=1] - Initial quantity
 * @param {boolean} [params.customModalBlocked=false] - Whether custom modal is blocked
 *
 * @returns {object} Purchase logic and handlers:
 *   - purchase: (event, openModal, customData, onPurchase) => void - Execute purchase flow
 *   - openCustomModal: (event, openModal, data) => void - Open custom modal or fall back to purchase
 *   - wrapHandlers: (modalProps) => object - Returns { handlePurchase, handleOpenCustomModal, ctaFunction }
 *   - btnBusy: boolean - Loading state during async add-to-cart
 *
 * @description
 * - Handles all product types: standard, preorder, supplies/replacement links, CTO, eCarepack
 * - Memoizes metrics and variant objects to prevent unnecessary re-renders
 * - wrapHandlers() binds modal context (openModal, customModalData, onPurchase) to handlers
 * - Prevents default behavior for CTO links and wraps custom onClick callbacks from parent
 * - All callbacks have complete dependency arrays to prevent stale closure bugs
 */
const usePdpCta = ({
    baseProductSku,
    xsellType,
    onClick,
    jumpid,
    additionalParams = EMPTY_OBJECT,
    isMultiple,
    qty,
    customModalBlocked = false,
    modalProps = null,
}) => {
    const history = useHistory();
    // withPurchaseButton HOC redux states
    const { productData, etrStoreData, userData, xSell, cartInfo, pdpCarePackBundle, productInitial, location } =
        useSelector(state => ({
            productData: state.productData,
            etrStoreData: state.etrStoreData,
            userData: state.userData,
            xSell: state.xSell,
            cartInfo: state.cartInfo,

            // slugInfo components
            pdpCarePackBundle:
                state && state.slugInfo && state.slugInfo.components && state.slugInfo.components.pdpCarePackBundle,
            productInitial:
                state && state.slugInfo && state.slugInfo.components && state.slugInfo.components.productInitial,

            // withPageComponents states
            location: state && state.router && state.router.location,
        }));

    // withConfigurations HOC redux states
    const { addOns, ctoConfigurations, ctoConfig, ctoLink } = useSelector(state => {
        let { addOns, ctoConfigurations } = state.productData;

        /**
         * If product prop provided, automatically map to that product
         */
        if (productInitial && productInitial.sku) {
            const ctoConfig = ctoConfigurations[productInitial.sku] || getDefaultState(productInitial);
            const id = productInitial.itemId || productInitial.catEntryId || productInitial.catentryId;
            return {
                addOns: addOns[productInitial.sku] || EMPTY_OBJECT,
                //TODO: don't overload ctoConfigurations, it's confusing
                ctoConfigurations: ctoConfig,
                ctoConfig,
                ctoLink: getCTOLink(id, 1, ctoConfig),
            };
        }
        return {
            addOns,
            ctoConfigurations,
            ctoConfig: EMPTY_OBJECT,
        };
    });

    // Redux hooks for dispatch
    const dispatch = useDispatch();
    const dispatchActions = useMemo(
        () => ({
            // withPurchaseButton HOC action creators
            updateCart: (item, qty) => dispatch(updateCart(item, qty)),
            updateCartBySKU: (sku, qty) => dispatch(updateCartBySKU(sku, qty)),
            addCTOToCart: (product, quantity, blockFlyout, followRedirect) =>
                dispatch(addCTOToCart(product, quantity, blockFlyout, followRedirect)),
            addToCart: (product, quantity, blockFlyout, addOns, options, baseProductSku, xsellType, metricParams) =>
                dispatch(
                    addToCart(product, quantity, blockFlyout, addOns, options, baseProductSku, xsellType, metricParams),
                ),
            addMultipleToCart: (products, quantity, blockFlyout, options, baseProductSku, xsellType) =>
                dispatch(addMultipleToCart(products, quantity, blockFlyout, options, baseProductSku, xsellType)),
            fetchXSellProducts: (product, limit, source, isV2) =>
                dispatch(fetchXSellProducts(product, limit, source, isV2)),
            fetchPrices: Helpers.memoize(id => dispatch(fetchPrices([id]))),
            confirmAddToCart: (confirmType, params) => dispatch(confirmAddToCart(confirmType, params)),
            launchCartFlyout: payload => dispatch(launchCartFlyout(payload)),

            // withConfigurations HOC action creators
            setAddOns: (sku, addOns) => dispatch(setAddOns(sku, addOns)),
            setSoftBundle: (sku, addOns) => dispatch(setSoftBundle(sku, addOns)),
            setCTOConfiguration: (sku, config) => dispatch(setCTOConfiguration(sku, config)),
        }),
        [dispatch],
    );

    const { cartId } = useCartState();

    // Derived state from Redux
    const { productInfo } = productData || {};
    const { labels: priceLabels } = etrStoreData || {};

    const isccf = (userData?.profileData || {}).isccf || false;
    const xSellModalBlocked = xSell?.blocked || false;
    const xSellModalIsOpen = xSell?.isOpen || false;
    const xSellOpenedSkus = xSell?.xSellOpenedSkus || EMPTY_OBJECT;
    const addToCartFlyoutVisible = cartInfo?.addToCartFlyoutVisible || false;

    const { carepacks, serviceBundle } = pdpCarePackBundle || EMPTY_OBJECT;

    const { prices } = useProductPrice([
        productInitial,
        ...(Array.isArray(carepacks) ? carepacks : []),
        ...(serviceBundle && Array.isArray(serviceBundle.carepacks) ? serviceBundle.carepacks : []),
    ]);

    const { sku } = productInitial || {};
    const price = productInitial && prices && prices[sku];
    const { forceOOS } = price || EMPTY_OBJECT;

    const serialNumber = getSerialNumber(productInitial, location);

    const {
        error,
        giftCardForm,
        configCatentryId,
        price: giftCardPrice = {},
        isNotDefaultColor,
        isNotDefaultConfig,
    } = ctoConfig || EMPTY_OBJECT;

    const variant = useMemo(() => {
        if (giftCardForm && !error) {
            return { ...giftCardForm, itemId: configCatentryId, price: giftCardPrice?.salePrice };
        }
        return EMPTY_OBJECT;
    }, [giftCardForm, error, configCatentryId, giftCardPrice]);

    let selectedAddon = addOns && getAddon(sku, addOns);

    const [quantity, setQuantity] = useState(() => (isMultiple ? qty : 1));
    const [customModalOnce, setCustomModalOnce] = useState(true);
    const [btnBusy, setBtnBusy] = useState(false);

    // TODO: Check if this is still needed
    const getConditionalMetrics = useCallback(() => {
        try {
            let list = document.querySelector('#pdpAddToCartBtn').getAttribute('data-add-to-cart-list');
            return list ? { list } : {};
        } catch (e) {}
        return {};
    }, []);

    const metrics = useMemo(
        () => ({
            list: 'pdp',
            forInlineAttribute: true,
            cartId,
            baseProductSku: sku,
            addons: (serviceBundle && serviceBundle.carepacks) || carepacks,
            addonMetrics: {
                xsellMethod: 'pdp-carepack',
                xsellProdInfo: sku,
            },
            selectedAddonSku: selectedAddon && selectedAddon.sku,
            isMainSKU: true,
        }),
        [cartId, sku, serviceBundle, carepacks, selectedAddon],
    );

    const customizeAndBuy = useCallback(() => {
        const { ctoLink } = price || {};
        const { color, configCatentryId, seoUrl } = ctoConfig || EMPTY_OBJECT;
        let params = additionalParams;
        if (jumpid) {
            params = { ...params, jumpid };
        }
        if (ctoConfig) {
            params = ctoLink?.indexOf('custom') < 0 ? { ...params, color, configCatentryId } : params;
            if (seoUrl) {
                history.push(seoUrl);
                return;
            }
        }
        history.push(Helpers.addUrlParam(ctoLink, params));
    }, [price, ctoConfig, jumpid, additionalParams, history]);

    const purchase = useCallback(
        (event, openModal, customData, onPurchase) => {
            const { Devdependency } = productInitial || EMPTY_OBJECT;
            const { suppliesLinkUrl } = productInitial.attributes || EMPTY_OBJECT;
            const prdClass = productInitial.product_type || productInitial.prdClass;

            // TEMP CODE SINCE GTM ISN'T IMPLEMENTED IN PRODUCTION
            let conditionalMetrics = {};
            try {
                conditionalMetrics = getConditionalMetrics();
            } catch (e) {}

            if (forceOOS || (price.isComingSoonSku && price.isOOS)) {
                return;
            } else if (price.preOrder) {
                return (
                    dispatchActions.confirmAddToCart &&
                    dispatchActions.confirmAddToCart('preorder', [
                        productInitial,
                        quantity,
                        addOns,
                        variant,
                        baseProductSku,
                        xsellType,
                        metrics,
                    ])
                );
            } else if (suppliesLinkUrl) {
                const { pdpConfig } = productInitial || {};
                const { pdpDetails } = pdpConfig || {};
                if (pdpDetails && pdpDetails.replacementProduct) {
                    window.location = `${suppliesSearchUrl}?searchTerm=${productInitial.sku}`;
                } else {
                    window.location = suppliesLinkUrl;
                }
            } else if (prdClass === 'CTO' && !productInitial.eol) {
                customizeAndBuy();
            } else if (
                Helpers.isECarepack(productInitial) &&
                !(baseProductSku || serialNumber) &&
                Devdependency !== 'NO'
            ) {
                dispatchActions.confirmAddToCart('ecarepack', [
                    productInitial,
                    quantity,
                    addOns,
                    variant,
                    baseProductSku,
                    xsellType,
                    metrics,
                ]);
            } else if (price.stock === undefined || price.stock > 0) {
                setBtnBusy(true);
                let metricParams = {
                    ...(metrics || {}),
                    ...(productInitial && !productInitial.price && { price }),
                    ...(conditionalMetrics || {}),
                };
                dispatchActions
                    .addToCart(
                        productInitial,
                        quantity,
                        true,
                        addOns,
                        { ...variant, userInputBaseSKU: serialNumber, removeExistingAssociation: true },
                        baseProductSku,
                        xsellType,
                        metricParams,
                    )
                    .then(() => {
                        setBtnBusy(false);
                        dispatchActions.launchCartFlyout({
                            analyticsData: {
                                gtmIdPrefix: 'cart-overlay',
                                ...(metrics || {}),
                            },
                        });
                    })
                    .catch(() => {
                        setBtnBusy(false);
                    });
            }
        },
        [
            productInitial,
            price,
            quantity,
            addOns,
            variant,
            customizeAndBuy,
            serialNumber,
            metrics,
            baseProductSku,
            xsellType,
            dispatchActions,
        ],
    );

    const openCustomModal = useCallback(
        (event, openModal, data) => {
            const { once } = data || {};
            if (customModalOnce) {
                setCustomModalOnce(!once);
                openModal();
            } else {
                purchase(event);
            }
        },
        [customModalOnce, purchase],
    );

    /**
     * Wraps CTA handlers with PurchaseModal render props (openModal, customModalData, onPurchase).
     * Returns ready-to-use onClick handlers that can be passed directly to child components.
     * Matches the pattern used in purchase-button-v2.jsx where cta_function is called with
     * all four arguments: event, openModal, customModalData, onPurchase.
     * Also handles custom onClick callbacks from parent components and prevents default for CTO links.
     *
     * @param {object} props - PurchaseModal render props { openModal, customModalData, onPurchase }
     * @returns {object} { handlePurchase, handleOpenCustomModal, ctaFunction }
     *   handlePurchase - onClick handler that executes purchase flow with modal context
     *   handleOpenCustomModal - onClick handler that opens custom modal or falls back to purchase
     *   ctaFunction - The CTA function to use (purchase or openCustomModal) based on customModalData
     */
    const wrapHandlers = useCallback(
        props => {
            const { openModal, customModalData, onPurchase } = props || {};

            const handlePurchase = event => {
                // Prevent default behavior if this is a CTO link
                if (ctoLink && event && typeof event.preventDefault === 'function') {
                    event.preventDefault();
                }

                if (typeof onClick === 'function') {
                    onClick(() => {
                        purchase(event, openModal, customModalData, onPurchase);
                    });
                } else {
                    purchase(event, openModal, customModalData, onPurchase);
                }
            };

            const handleOpenCustomModal = event => {
                // Prevent default behavior if this is a CTO link
                if (ctoLink && event && typeof event.preventDefault === 'function') {
                    event.preventDefault();
                }

                if (typeof onClick === 'function') {
                    onClick(() => {
                        openCustomModal(event, openModal, customModalData);
                    });
                } else {
                    openCustomModal(event, openModal, customModalData);
                }
            };

            // Determine CTA function based on customModalData presence
            const ctaFunction = customModalData ? handleOpenCustomModal : handlePurchase;

            return {
                handlePurchase,
                handleOpenCustomModal,
                ctaFunction,
            };
        },
        [purchase, openCustomModal, onClick, ctoLink],
    );

    return {
        // CTA functions
        purchase,
        openCustomModal,
        wrapHandlers,
        btnBusy,
    };
};

export default usePdpCta;
